import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "react-query";

import {
  IUsuarioLogadoInfoReserva,
  ReservaService,
} from "../services/api/reserva/ReservaService";
import { feedback } from "../../shared/services";

interface IUsuarioLogadoInfoReservaContextData {
  usuarioLogadoInfo: IUsuarioLogadoInfoReserva | null;
  isLoadingUsuarioLogadoInfo: boolean;
  isErrorUsuarioLogadoInfo: boolean;
  idTenant: string;
}

const UsuarioLogadoInfoReservaContext = createContext<
  IUsuarioLogadoInfoReservaContextData | undefined
>(undefined);

interface IUsuarioLogadoInfoReservaProviderProps {
  children: React.ReactNode;
}

export const UsuarioLogadoInfoReservaProvider: React.FC<
  IUsuarioLogadoInfoReservaProviderProps
> = ({ children }) => {
  // provider fica fora do BrowserRouter, então lê os params direto da URL
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const tenant = params.get("tenant") || "";

  const {
    data: usuarioLogadoInfo,
    isLoading: isLoadingUsuarioLogadoInfo,
    isError: isErrorUsuarioLogadoInfo,
  } = useQuery(
    ["reserva-usuario-logado-info", token, tenant],
    async () => {
      const result = await ReservaService.getUsuarioLogadoInfo({
        token,
        tenant,
      });

      if (result instanceof Error) {
        throw result;
      }

      return result;
    },
    {
      retry: false,
      staleTime: Infinity,
      cacheTime: Infinity,
      onError: (error: unknown) =>
        feedback("Erro ao consultar usuário logado.", "error"),
    },
  );

  const value = useMemo(
    () => ({
      usuarioLogadoInfo: usuarioLogadoInfo ?? null,
      isLoadingUsuarioLogadoInfo,
      isErrorUsuarioLogadoInfo,
      idTenant: usuarioLogadoInfo?.idTenant ?? "",
    }),
    [isErrorUsuarioLogadoInfo, isLoadingUsuarioLogadoInfo, usuarioLogadoInfo],
  );

  return (
    <UsuarioLogadoInfoReservaContext.Provider value={value}>
      {children}
    </UsuarioLogadoInfoReservaContext.Provider>
  );
};

/**
 * Disponibiliza os dados do endpoint de usuário logado da reserva para uso global.
 */
export const useUsuarioLogadoInfoReserva = () => {
  const context = useContext(UsuarioLogadoInfoReservaContext);

  if (!context) {
    throw new Error(
      "useUsuarioLogadoInfoReserva deve ser utilizado dentro de UsuarioLogadoInfoReservaProvider.",
    );
  }

  return context;
};

/**
 * Hook com nome simplificado para consumo do contexto de usuário logado.
 */
export const useUsuarioLogadoContext = () => useUsuarioLogadoInfoReserva();
